"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { loginTeacher } from "./api"
import studentImage from "../resources/4884388.jpg"
import teacherImage from "../resources/4905784.jpg"
import { ArrowLeft, Loader, X, Clock, Eye, EyeOff, CheckCircle } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { postAws } from "../postAws"
import { requestNotificationPermission, saveTokenToDatabase } from "./fcm-api"

export default function LoginPage() {
  const [isStudent, setIsStudent] = useState(false)
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    disabled: false,
  })
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState({ value: "", disabled: false })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loginError, setLoginError] = useState("")
  const [forgotPasswordError, setForgotPasswordError] = useState("")
  const [success, setSuccess] = useState("")
  const navigate = useNavigate()
  const [showOtpModal, setShowOtpModal] = useState(false)
  const [otp, setOtp] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmNewPassword, setConfirmNewPassword] = useState("")
  const [timeLeft, setTimeLeft] = useState(300)
  const [isTimedOut, setIsTimedOut] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [otpError, setOtpError] = useState("")
  const [passwordError, setPasswordError] = useState("")
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const token = localStorage.getItem("token")
    const teacherId = localStorage.getItem("teacherId")
    if (token && teacherId) {
      navigate("/dashboard")
    }
  }, [navigate])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoginError("") // Reset login error message
    setIsLoading(true)
    setFormData((prevData) => ({ ...prevData, disabled: true }))
    try {
      const data = await loginTeacher(formData.email, formData.password)
      console.log("Login successful:", data)
      if (data.token && data.teacherId) {
        // Store login data first to ensure user can proceed even if FCM fails
        localStorage.setItem("token", data.token)
        localStorage.setItem("teacherId", data.teacherId)

        // After successful login, get the FCM token and save it
        try {
          // Request notification permission and get FCM token
          const fcmToken = await requestNotificationPermission()
          if (fcmToken) {
            // Store FCM token in localStorage for later use (needed for logout)
            localStorage.setItem("fcmToken", fcmToken)

            // Save FCM token to server using the updated Lambda function
            saveTokenToDatabase(data.teacherId, fcmToken)
              .then((saveResult) => {
                console.log("FCM token saved to server:", saveResult)
                // If saving failed, schedule a retry
                if (!saveResult) {
                  console.log("Scheduling token save retry")
                  setTimeout(() => {
                    saveTokenToDatabase(data.teacherId, fcmToken)
                      .then((retryResult) => console.log("FCM token retry result:", retryResult))
                      .catch((err) => console.error("FCM token retry error:", err))
                  }, 5000)
                }
              })
              .catch((err) => console.error("Error saving FCM token:", err))
          } else {
            console.warn("Failed to get FCM token during login")
          }
        } catch (fcmError) {
          console.error("Error with FCM during login:", fcmError)
          // Continue with login even if FCM fails
        }

        // Navigate to dashboard regardless of FCM status
        navigate("/dashboard")
      } else {
        throw new Error("Login successful, but required data is missing")
      }
    } catch (error) {
      setLoginError("Login failed. Please check your credentials and try again.")
    } finally {
      setIsLoading(false)
      setFormData((prevData) => ({ ...prevData, disabled: false }))
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    setForgotPasswordError("") // Reset forgot password error message
    setIsSubmitting(true)
    setSuccess("")
    setForgotPasswordEmail((prev) => ({ ...prev, disabled: true }))

    try {
      const forgotPasswordData = {
        body: { teacherEmail: forgotPasswordEmail.value },
        request: "forgot-password-Teacher",
      }
      const response = await postAws(`https://api-dev.aurait.ai/forgotpassword/teacher`, "POST", forgotPasswordData)

      if (response.statusCode === 200) {
        setShowOtpModal(true)
        setTimeLeft(300)
        setIsTimedOut(false)
      } else if (response.statusCode === 400) {
        setForgotPasswordError(response.body.error)
      } else {
        throw new Error("Unexpected error occurred")
      }
    } catch (err) {
      setForgotPasswordError("Failed to send OTP. Please try again.")
    } finally {
      setIsSubmitting(false)
      setForgotPasswordEmail((prev) => ({ ...prev, disabled: false }))
    }
  }

  const handleOtpVerification = async (e) => {
    e.preventDefault()
    setIsVerifying(true)
    setOtpError("")
    setPasswordError("")

    if (newPassword !== confirmNewPassword) {
      setPasswordError("Passwords do not match")
      setIsVerifying(false)
      return
    }

    try {
      const resetPasswordData = {
        body: {
          teacherEmail: forgotPasswordEmail.value,
          otp: otp,
          password: newPassword,
        },
        request: "verify-otp-Teacher",
      }
      const response = await postAws(`https://api-dev.aurait.ai/forgotpassword/teacher`, "POST", resetPasswordData)

      if (response.statusCode === 401) {
        setOtpError(response.body.error || "Invalid OTP, please try again")
      } else if (response.statusCode === 201) {
        setShowOtpModal(false)
        setShowForgotPasswordModal(false)
        setShowSuccessModal(true)
        setTimeout(() => {
          setShowSuccessModal(false)
          navigate("/login")
        }, 10000)
      } else {
        throw new Error("Unexpected response from server")
      }
    } catch (error) {
      setOtpError("An error occurred. Please try again.")
    } finally {
      setIsVerifying(false)
    }
  }

  const handleResendOtp = async () => {
    setIsResending(true)
    await handleForgotPassword({ preventDefault: () => {} })
    setIsResending(false)
    setTimeLeft(300)
    setIsTimedOut(false)
    setOtpError("")
  }

  useEffect(() => {
    let timer
    if (showOtpModal && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timer)
            setIsTimedOut(true)
            return 0
          }
          return prevTime - 1
        })
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [showOtpModal, timeLeft])

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`
  }

  const closeAllModals = () => {
    setShowForgotPasswordModal(false)
    setShowOtpModal(false)
    setShowSuccessModal(false)
  }

  return (
    <div className="flex flex-col items-center w-full min-h-screen justify-between">
      {/* Header */}
      <header className="bg-white shadow-md rounded-md top-0 z-50 w-full py-2 opacity-95 flex justify-center">
        <div className="w-full px-4 sm:w-11/12 md:w-4/5 lg:w-3/4 flex items-center justify-between">
          <div className="logo-name">
            <Link to={"/"} className="flex items-center gap-2 py-2">
              <div>
                <h1 className="flex text-2xl sm:text-3xl font-semibold">
                  <span>पढ़.</span>
                  <span className="gradient-text font-semibold">AI</span>
                </h1>
              </div>
            </Link>
          </div>
          <div className="flex gap-2 sm:gap-4 items-center">
            <Link
              to="/signup"
              className="border-gray-600 border-2 rounded-xl bg-white text-gray-800 font-semibold text-sm sm:text-lg py-1 px-3 sm:px-6 rounded"
            >
              Sign up
            </Link>
            <Link to="/login" className="gradient-border">
              <button className="bg-white text-gray-800 font-semibold text-sm sm:text-lg py-1 px-3 sm:px-6 rounded focus:outline-none focus:shadow-outline rounded-xl">
                Login
              </button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-col items-center justify-center p-4 flex-grow w-full">
        <div className="w-full max-w-4xl">
          {/* Toggle Button */}
          <div className={`w-full mb-4 flex ${isStudent ? "justify-start" : "justify-end"}`}>
            <button className="bg-indigo-600 text-white px-4 sm:px-6 py-2 rounded-md shadow-md hover:bg-indigo-700 transition-colors duration-200 flex items-center gap-2 text-sm sm:text-base">
              {isStudent ? (
                <>
                  <ArrowLeft className="w-4 h-4" />
                  Login as Teacher
                </>
              ) : (
                <a className="flex items-center" href="https://https://padhai-student.aurait.ai/login">
                  <ArrowLeft className="w-4 h-4" />
                  Login as Student
                </a>
              )}
            </button>
          </div>

          {/* Login Card */}
          <div
            className={`w-full bg-white rounded-lg shadow-xl overflow-hidden ${isStudent ? "student-mode" : "teacher-mode"}`}
          >
            <div className="flex flex-col md:flex-row">
              {/* Form Section */}
              <div className={`w-full md:w-1/2 p-4 sm:p-6 md:p-8 ${isStudent ? "order-last" : "order-first"}`}>
                <div className="mb-6">
                  <h2 className="text-2xl sm:text-3xl font-semibold mb-1">Login</h2>
                  <p className="text-base sm:text-lg font-light mb-1">
                    {isStudent ? "Welcome Back Learner!" : "Welcome Back Educator!"}
                  </p>
                  <div className="flex gap-1 text-xs sm:text-sm">
                    <span className="text-gray-600">New?</span>
                    <Link to="/signup" className="text-indigo-600 hover:text-indigo-800">
                      Sign Up
                    </Link>
                    <span className="text-gray-600">instead.</span>
                  </div>
                </div>
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                      disabled={formData.disabled}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      required
                      disabled={formData.disabled}
                    />
                  </div>
                  {loginError && <div className="text-red-600 text-sm">{loginError}</div>}
                  {success && <div className="text-green-600 text-sm">{success}</div>}
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    disabled={formData.disabled}
                  >
                    {isLoading ? <Loader className="animate-spin h-5 w-5 mr-2" /> : "Login"}
                  </button>
                  <div className="mt-2 text-center">
                    <button
                      type="button"
                      onClick={() => {
                        setLoginError("") // Reset login error message
                        setForgotPasswordError("") // Reset forgot password error message
                        setShowForgotPasswordModal(true)
                      }}
                      className="text-indigo-600 hover:text-indigo-800 text-sm"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </form>
              </div>

              {/* Image Section */}
              <div className={`w-full md:w-1/2 ${isStudent ? "order-first" : "order-last"}`}>
                <div className="h-48 sm:h-64 md:h-full">
                  <img
                    src={isStudent ? studentImage : teacherImage}
                    alt={isStudent ? "Student" : "Teacher"}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white w-full py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex gap-2 items-center">
              <span className="text-xl sm:text-2xl font-bold">Padhai </span>
              <span className="text-sm sm:text-lg font-medium">An AURA-AI Initiative</span>
            </div>
            <div className="text-center text-gray-400 text-sm">© 2025 Padhai. All rights reserved.</div>
            <nav className="w-full md:w-auto">
              <ul className="flex flex-wrap justify-center md:justify-end gap-3 sm:gap-4">
                <li>
                  <a href="#" className="hover:text-indigo-400 text-xs sm:text-sm">
                    About
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-400 text-xs sm:text-sm">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-400 text-xs sm:text-sm">
                    Privacy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-indigo-400 text-xs sm:text-sm">
                    Terms
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </footer>

      {/* Forgot Password Modal */}
      {showForgotPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl relative max-w-md w-full">
            <button onClick={closeAllModals} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700">
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Forgot Password</h2>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label htmlFor="forgotPasswordEmail" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  id="forgotPasswordEmail"
                  value={forgotPasswordEmail.value}
                  onChange={(e) => setForgotPasswordEmail((prev) => ({ ...prev, value: e.target.value }))}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  required
                  disabled={forgotPasswordEmail.disabled}
                />
              </div>
              {forgotPasswordError && <div className="text-red-600 text-sm">{forgotPasswordError}</div>}
              {success && <div className="text-green-600 text-sm">{success}</div>}
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                    Sending...
                  </>
                ) : (
                  "Request OTP"
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* OTP Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl relative max-w-md w-full">
            <button onClick={closeAllModals} className="absolute top-2 right-2 text-gray-500 hover:text-gray-700">
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Reset Password</h2>
            <p className="mb-4 text-sm sm:text-base">Enter the OTP sent to your email: {forgotPasswordEmail.value}</p>
            {!isTimedOut && <p className="mb-4 text-sm">Valid for 5 minutes</p>}
            <form onSubmit={handleOtpVerification} className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                  OTP
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter OTP"
                  required
                  disabled={isTimedOut}
                />
              </div>
              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  New Password
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full pr-10 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <input
                    type={showConfirmNewPassword ? "text" : "password"}
                    id="confirmNewPassword"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="block w-full pr-10 border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  >
                    {showConfirmNewPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              {otpError && <p className="mt-1 text-sm text-red-600">{otpError}</p>}
              {passwordError && <p className="mt-1 text-sm text-red-600">{passwordError}</p>}
              {isTimedOut ? (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center justify-center"
                  disabled={isResending}
                >
                  {isResending ? (
                    <>
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Resending...
                    </>
                  ) : (
                    "Resend OTP"
                  )}
                </button>
              ) : (
                <button
                  type="submit"
                  className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center justify-center"
                  disabled={isVerifying}
                >
                  {isVerifying ? (
                    <>
                      <Loader className="animate-spin h-5 w-5 mr-2" />
                      Verifying...
                    </>
                  ) : (
                    "Reset Password"
                  )}
                </button>
              )}
            </form>
            <div className="mt-4 flex items-center justify-end">
              <Clock className="h-5 w-5 text-gray-500 mr-2" />
              <span className={`text-gray-500 ${isTimedOut ? "text-red-500" : ""}`}>
                {isTimedOut ? "OTP Timed Out" : formatTime(timeLeft)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 sm:p-8 rounded-lg shadow-xl text-center max-w-sm w-full">
            <CheckCircle className="h-12 w-12 sm:h-16 sm:w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Password Reset Successful</h2>
            <p className="mb-4 text-sm sm:text-base">Your password has been updated successfully.</p>
            <p className="text-gray-500 text-sm">Redirecting to login page in 10 seconds...</p>
          </div>
        </div>
      )}
    </div>
  )
}
